/**
 * Dynamic CSS Color Extraction - Reads color variables directly from Layout.css at runtime
 * Single source of truth approach - no need to maintain separate color mappings
 */

/**
 * Extract all CSS custom properties that appear to be color-related
 */
const extractCSSColorVariables = () => {
  const element = document.documentElement
  const computedStyle = getComputedStyle(element)
  const colorVariables = new Map()

  // Get all CSS custom property names from stylesheets
  const styleSheets = Array.from(document.styleSheets)
  const customPropertyNames = new Set()

  try {
    styleSheets.forEach(styleSheet => {
      try {
        const rules = Array.from(styleSheet.cssRules || styleSheet.rules || [])
        rules.forEach(rule => {
          if (rule.style) {
            for (let i = 0; i < rule.style.length; i++) {
              const prop = rule.style[i]
              if (prop.startsWith('--color-')) {
                customPropertyNames.add(prop)
              }
            }
          }
        })
      } catch (e) {
        // Skip stylesheets that can't be accessed (CORS)
        console.warn('Could not access stylesheet:', e)
      }
    })
  } catch (e) {
    console.warn('Error accessing stylesheets:', e)
  }

  // Fallback: try to get variables from computed style
  // This catches variables that might not be accessible via cssRules
  const fallbackVariables = [
    '--color-primary', '--color-primary_lt', '--color-primary_dk', '--color-primary_hov',
    '--color-secondary', '--color-primary_alt',
    '--color-gray_1', '--color-gray_2', '--color-gray_3', '--color-gray_4',
    '--color-canvas', '--color-paper', '--color-footer', '--color-navbar', '--color-sidebar',
    '--color-neutral', '--color-neutral_1', '--color-neutral_2', '--color-neutral_3', '--color-bordered',
    '--color-text', '--color-text_inv', '--color-text_secondary',
    '--color-success', '--color-error', '--color-progress', '--color-warning', '--color-link',
    '--color-theme_white', '--color-theme_black', '--color-white_to_black',
    '--color-cs_user', '--color-cs_agent',
    '--color-admin_primary', '--color-admin_primary_lt', '--color-admin_primary_dk',
    '--color-admin_secondary', '--color-admin_canvas', '--color-admin_paper',
    '--color-admin_bordered', '--color-admin_neutral', '--color-admin_neutral_1',
    '--color-admin_neutral_2', '--color-admin_neutral_3', '--color-admin_success',
    '--color-admin_progress', '--color-admin_error', '--color-admin_warning',
    '--color-admin_link', '--color-admin_text', '--color-admin_text_inv',
    '--color-admin_text_secondary', '--color-admin_black', '--color-admin_white_to_black',
    '--color-admin_cs_user', '--color-admin_cs_agent'
  ]

  fallbackVariables.forEach(varName => customPropertyNames.add(varName))

  // Get computed values for all found variables
  customPropertyNames.forEach(varName => {
    const value = computedStyle.getPropertyValue(varName).trim()
    if (value) {
      // Convert space-separated RGB values to proper rgb() format
      const rgbMatch = value.match(/^(\d+)\s+(\d+)\s+(\d+)$/)
      const computedValue = rgbMatch 
        ? `rgb(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]})`
        : value

      colorVariables.set(varName, computedValue)
    }
  })

  return colorVariables
}

/**
 * Generate CSS class names from color variable names
 */
const generateColorClassNames = (colorVariables) => {
  const classNames = []
  
  colorVariables.forEach((value, varName) => {
    // Remove --color- prefix and convert to class name
    const colorName = varName.replace('--color-', '')
    
    // Generate text color class
    const textClass = `text-${colorName}`
    classNames.push({
      className: textClass,
      cssVariable: varName,
      computedValue: value,
      type: 'text'
    })
  })

  return classNames
}

/**
 * Group color classes by category for better organization
 */
const groupColorClasses = (colorClasses) => {
  const groups = {
    'Primary': [],
    'Gray': [],
    'Admin': [],
    'Status': [],
    'Theme': [],
    'Layout': [],
    'Neutral': [],
    'Text': [],
    'Support': []
  }

  colorClasses.forEach(colorClass => {
    const { className } = colorClass
    
    if (className.includes('admin_')) {
      groups.Admin.push(colorClass)
    } else if (className.includes('gray_')) {
      groups.Gray.push(colorClass)
    } else if (className.includes('primary') || className.includes('secondary')) {
      groups.Primary.push(colorClass)
    } else if (className.includes('success') || className.includes('error') || 
               className.includes('warning') || className.includes('progress')) {
      groups.Status.push(colorClass)
    } else if (className.includes('theme_') || className.includes('white_to_black')) {
      groups.Theme.push(colorClass)
    } else if (className.includes('canvas') || className.includes('paper') || 
               className.includes('footer') || className.includes('navbar') || 
               className.includes('sidebar')) {
      groups.Layout.push(colorClass)
    } else if (className.includes('neutral') || className.includes('bordered')) {
      groups.Neutral.push(colorClass)
    } else if (className.includes('text') && !className.includes('admin_')) {
      groups.Text.push(colorClass)
    } else if (className.includes('cs_')) {
      groups.Support.push(colorClass)
    } else {
      groups.Primary.push(colorClass) // Default fallback
    }
  })

  // Remove empty groups and sort within groups
  Object.keys(groups).forEach(key => {
    if (groups[key].length === 0) {
      delete groups[key]
    } else {
      groups[key].sort((a, b) => a.className.localeCompare(b.className))
    }
  })

  return groups
}

/**
 * Cache for extracted colors to avoid re-computation
 */
let colorCache = null
let lastExtractionTime = 0
const CACHE_DURATION = 5000 // 5 seconds

/**
 * Get all available CSS color classes (with caching)
 */
export const getCSSColorClasses = () => {
  const now = Date.now()
  
  if (!colorCache || (now - lastExtractionTime) > CACHE_DURATION) {
    try {
      const colorVariables = extractCSSColorVariables()
      const colorClasses = generateColorClassNames(colorVariables)
      colorCache = {
        classes: colorClasses,
        grouped: groupColorClasses(colorClasses),
        variables: colorVariables
      }
      lastExtractionTime = now
    } catch (error) {
      console.error('Error extracting CSS colors:', error)
      return []
    }
  }

  return colorCache.classes.map(c => c.className)
}

/**
 * Get grouped CSS color classes for better organization
 */
export const getGroupedCSSColorClasses = () => {
  const now = Date.now()
  
  if (!colorCache || (now - lastExtractionTime) > CACHE_DURATION) {
    getCSSColorClasses() // This will populate the cache
  }

  return colorCache ? colorCache.grouped : {}
}

/**
 * Search CSS color classes by name
 */
export const searchCSSColorClasses = (searchTerm) => {
  const classes = getCSSColorClasses()
  const term = searchTerm.toLowerCase()
  return classes.filter(className => 
    className.toLowerCase().includes(term)
  )
}

/**
 * Get the computed color value for a specific CSS class name
 */
export const getComputedCSSColor = (className) => {
  const now = Date.now()
  
  if (!colorCache || (now - lastExtractionTime) > CACHE_DURATION) {
    getCSSColorClasses() // This will populate the cache
  }

  if (!colorCache) return null

  const colorClass = colorCache.classes.find(c => c.className === className)
  return colorClass ? colorClass.computedValue : null
}

/**
 * Get CSS variable name for a class name
 */
export const getCSSVariableForClass = (className) => {
  const now = Date.now()
  
  if (!colorCache || (now - lastExtractionTime) > CACHE_DURATION) {
    getCSSColorClasses() // This will populate the cache
  }

  if (!colorCache) return null

  const colorClass = colorCache.classes.find(c => c.className === className)
  return colorClass ? colorClass.cssVariable : null
}

/**
 * Test if a color value matches a CSS variable
 */
export const testColorValue = (testColor) => {
  // Create a temporary element to test color rendering
  const tempElement = document.createElement('div')
  tempElement.style.color = testColor
  document.body.appendChild(tempElement)
  const computedColor = getComputedStyle(tempElement).color
  document.body.removeChild(tempElement)
  
  return computedColor
}

/**
 * Get all CSS color information (for debugging)
 */
export const getColorDebugInfo = () => {
  const colorVariables = extractCSSColorVariables()
  const colorClasses = generateColorClassNames(colorVariables)
  
  return {
    variables: Array.from(colorVariables.entries()),
    classes: colorClasses,
    grouped: groupColorClasses(colorClasses)
  }
}

/**
 * Force refresh of color cache (useful when CSS is dynamically updated)
 */
export const refreshColorCache = () => {
  colorCache = null
  lastExtractionTime = 0
  return getCSSColorClasses()
}

export default {
  getCSSColorClasses,
  getGroupedCSSColorClasses,
  searchCSSColorClasses,
  getComputedCSSColor,
  getCSSVariableForClass,
  refreshColorCache,
  getColorDebugInfo
}